import { useRef, type MouseEvent, type ReactNode } from 'react';

export function MagneticCta({
  href,
  onClick,
  children,
  className = '',
  variant = 'primary',
}: Props) {
  const buttonRef = useRef<HTMLAnchorElement>(null);

  const handleMouseMove = (event: MouseEvent<HTMLAnchorElement>) => {
    const element = buttonRef.current;

    if (!element) {
      return;
    }

    const rect = element.getBoundingClientRect();
    const x = event.clientX - rect.left - rect.width / 2;
    const y = event.clientY - rect.top - rect.height / 2;

    element.style.transform = `translate(${x * 0.16}px, ${y * 0.16}px)`;
  };

  const handleMouseLeave = () => {
    const element = buttonRef.current;

    if (element) {
      element.style.transform = 'translate(0px, 0px)';
    }
  };

  const variantClasses = {
    primary:
      'border border-primary-600 bg-primary-600 text-bone hover:border-primary-500 hover:bg-primary-500',
    secondary:
      'border border-primary-300 bg-primary-50 text-primary-700 hover:border-primary-400 hover:bg-primary-100',
    ghost: 'border border-neutral-300 bg-transparent text-ink hover:border-neutral-400 hover:bg-neutral-100',
  };

  return (
    <a
      ref={buttonRef}
      href={href}
      onClick={onClick}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className={`inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg px-6 py-3.5 font-body text-sm font-medium tracking-wide ${variantClasses[variant]} ${className}`}
      style={{
        transform: 'translate(0px, 0px)',
        transition:
          'transform 0.25s cubic-bezier(0.22, 1, 0.36, 1), background-color 0.3s, border-color 0.3s',
      }}
    >
      {children}
    </a>
  );
}

interface Props {
  href?: string;
  onClick?: () => void;
  children: ReactNode;
  className?: string;
  variant?: 'primary' | 'secondary' | 'ghost';
}
